import {
  color,
  focusStyles,
  label,
  radius,
  reset,
  spacing,
  TmElement,
  token,
} from '@thiememeulenhoff/lit-sdk';
import { PropertyColor } from '@thiememeulenhoff/types';
import { css, html } from 'lit';
import {
  customElement,
  property,
  queryAssignedElements,
} from 'lit/decorators.js';
import { tagName } from '../../../helpers';
import { ToggleIndicatorComponent } from '../toggle-indicator/toggle-indicator.component';

const tag = tagName('toggle-button');

@customElement(tag)
export class ToggleButtonComponent extends TmElement {
  static override styles = [
    reset.button,
    css`
      :where(*) {
        box-sizing: border-box;
      }

      :host {
        ${label('medium')};

        color: ${color('grey', 600)};
        inline-size: 100%;
      }

      :host([active]) {
        color: ${color('white')};
      }

      :host([active][color='white']) {
        color: ${token('theme-base-color')};
      }

      .toggle-button[disabled] {
        cursor: not-allowed;
      }

      .toggle-button {
        align-items: center;
        color: inherit;
        cursor: pointer;
        display: flex;
        flex: 1;
        gap: ${spacing(100)};
        inline-size: 100%;
        justify-content: center;
        padding: ${spacing(100, 200)};
        text-decoration: none;
        white-space: nowrap;

        &:focus-visible {
          ${focusStyles};
          border-radius: ${radius('special')};
        }
      }
    `,
  ];

  @property({ type: Boolean, reflect: true })
  active = false;

  @property({ type: Boolean, reflect: true })
  disabled = false;

  @property({ type: String, reflect: true })
  color: PropertyColor = 'white';

  @property({ type: String })
  value = '';

  @property({ type: String })
  href = null;

  @queryAssignedElements({ selector: 'a-toggle-indicator' })
  assignedElements!: Array<ToggleIndicatorComponent>;

  setActive(active: boolean) {
    this.active = active;

    this.assignedElements.forEach((element) => {
      element.setActive(active);
    });
  }

  #handleClick = (event: Event) => {
    event.preventDefault();

    if (this.active) {
      return;
    }

    this.dispatchLocalEvent({
      eventName: 'toggle-button-selected',
      detail: this.value,
      cancelable: true,
    });
  };

  override render() {
    if (this.href) {
      return html`
        <a
          ?active="${this.active}"
          ?disabled="${this.disabled}"
          @click="${this.#handleClick}"
          data-test="toggle-button"
          class="toggle-button"
          href="${this.href}"
          part="button ${this.active ? 'active' : ''}"
          aria-pressed="${this.active}"
          aria-label="${this.active
            ? 'active'
            : 'inactive'}, ${this.textContent?.trim()}"
        >
          <slot></slot>
        </a>
      `;
    }

    return html`
      <button
        ?active="${this.active}"
        ?disabled="${this.disabled}"
        @click="${this.#handleClick}"
        data-test="toggle-button"
        class="toggle-button"
        part="button ${this.active ? 'active' : ''}"
        aria-pressed="${this.active}"
        aria-label="${this.active
          ? 'active'
          : 'inactive'}, ${this.textContent?.trim()}"
      >
        <slot></slot>
      </button>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'a-toggle-button': ToggleButtonComponent;
  }
}
